/**
 * Sprint 2 CP8 — PlaybackStore.
 *
 * <p>Guarda o estado de execução por tier:
 * <ul>
 *   <li><b>currentTier</b> — resolvido por {@link resolveTier} a partir do
 *       sync response. Default: 4 (legado, antes do primeiro sync).</li>
 *   <li><b>currentSlotSequence</b> — sequência ativa em Tier 2/3, null em
 *       Tier 1/4.</li>
 *   <li><b>currentSlotIndex</b> — índice do slot atual; será usado pelo
 *       loop de execução (CP9). Reset automático quando slot_sequence é
 *       substituída (top-of-hour ou troca de playlist).</li>
 * </ul>
 *
 * <p><b>Decisão de design (CP8):</b> a store é PASSIVA — só guarda o que
 * o backend manda. Loop de execução de Tier 2/3 vem no CP9. Pra Tier 4
 * (legado), {@link StreamStore} segue como hoje (currentSlotSequence=null).
 * Zero regressão pra players Tier 4 mesmo se essa store tiver bug.</p>
 *
 * <p><b>Hidratação:</b> {@link setFromSync} é chamado pelo SyncStore após
 * cada sync bem-sucedido. Reset de slotIndex quando a sequência muda
 * (comparação por playlistId + hourWindowStart — top-of-hour reseta).</p>
 */
import { makeAutoObservable } from 'mobx';
import type { SlotSequenceDto } from '~/api/types/sync';
import type { PlaylistItem } from '~/services/scheduler/types';
import type { Slot, SlotSequence, Tier } from '~/services/playback/types';
import { resolveTier } from '~/services/playback/TierResolver';
import { transformSlotSequence } from '~/api/transforms/mediaSync';
import analyticsService from '~/services/analytics/AnalyticsService';
import { otaLog } from '~/services/ota/OtaDebugLogger';
import {
  persistSlotSequence,
  clearSlotSequenceCache,
} from '~/services/playback/slotSequenceCache';

class PlaybackStore {
  currentTier: Tier = 4;
  currentSlotSequence: SlotSequence | null = null;
  currentSlotIndex: number = 0;
  /** ISO timestamp do último sync que hidratou esta store. */
  lastHydrationAt: string | null = null;
  /**
   * A ÉPOCA canônica que o server carimbou neste sync (`playback_started_at`), ou null quando
   * ele não ligou o sync pra este player.
   *
   * <p>É o MESMO valor que o caminho legado (Tier 4) usa nos seus itens. Guardar o valor em vez
   * de um booleano é o que impede a frota de ter DUAS épocas — vide {@link canonicalEpoch}.</p>
   */
  canonicalEpochValue: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * Janela (ms) em que um sync subsequente NÃO pode resetar o slotIndex
   * pra 0 — proteção pra o caso de o backend devolver hourWindowStart/
   * playlistId ligeiramente diferentes entre o disparo do trigger e a
   * retomada da campanha (top-of-hour aleatório, recálculo no servidor,
   * etc). Ver {@link restoreSlotIndex}.
   */
  private static readonly POST_RESTORE_GUARD_MS = 10_000;
  private lastRestoreAt: number = 0;

  /**
   * Atualiza a store a partir do {@code slot_sequence} do sync response.
   * Idempotente — chamar múltiplas vezes com o mesmo payload não tem
   * efeito colateral além de atualizar {@link lastHydrationAt}.
   *
   * Reset do {@link currentSlotIndex}: dispara quando a sequência muda
   * (playlistId diferente, ou slots com outro conteúdo/duração). A virada da
   * hora NÃO conta: a posição no loop vem da época canônica, não da janela
   * horária — zerar o índice ali jogava o player no slot 0 a cada hora e o pulso
   * tinha que corrigir 2s depois (soluço visível, medido no grupo 15, out/2026).
   *
   * Exceção: se {@link restoreSlotIndex} foi chamado nos últimos 10s
   * (trigger acabou de devolver o slot), **NÃO resetamos** mesmo com
   * seqChanged=true. Sem isso, o sync que chega ~1s após o
   * trigger_deactivated zera o slot pro 0 (bug reportado pelo Renato
   * 2026-05-27: Pepsi começa, em <1s volta pro Prada).
   */
  setFromSync(
    slotSequenceDto: SlotSequenceDto | null | undefined,
    options?: { preserveEmptySlots?: boolean; canonicalEpoch?: string | null },
  ): void {
    const tier = resolveTier(slotSequenceDto);
    let newSeq = transformSlotSequence(slotSequenceDto);
    const preserveEmptySlots = options?.preserveEmptySlots !== false;
    // `undefined` = o sync não trouxe informação sobre isso (short-circuit de
    // `update_required=no`, que reinjeta a slot_sequence mas NÃO manda `files[]`). Sobrescrever
    // com `false` ali desligaria a âncora a cada ~1min e o videowall ficaria entrando e saindo
    // de fase. Só um sync completo muda este estado.
    if (options?.canonicalEpoch !== undefined) {
      this.canonicalEpochValue = options.canonicalEpoch;
    }

    // Server pode devolver slot_sequence tier=3 com slots=[] transientemente
    // (race orientação / emergência). Preserva sequência anterior na mesma playlist.
    // EXCEÇÃO (Renato 2026-06-01): rotação física / forceResync passa
    // preserveEmptySlots=false — slots=[] em retrato significa "sem conteúdo
    // portrait na playlist"; manter landscape stale faz cache atualizar mas
    // Stream continuar tocando paisagem (bug Tab A7).
    if (
      preserveEmptySlots &&
      newSeq &&
      newSeq.slots.length === 0 &&
      this.currentSlotSequence &&
      this.currentSlotSequence.slots.length > 0 &&
      newSeq.playlistId === this.currentSlotSequence.playlistId
    ) {
      otaLog.warn('PlaybackStore setFromSync: preservando slots (sync vazio)', {
        playlistId: newSeq.playlistId,
        prevCount: this.currentSlotSequence.slots.length,
      });
      newSeq = {
        ...newSeq,
        slots: this.currentSlotSequence.slots,
      };
    }

    const seqChanged =
      this.didSequenceChange(this.currentSlotSequence, newSeq) ||
      this.didSequenceContentChange(this.currentSlotSequence, newSeq);
    const tierChanged = this.currentTier !== tier;
    const prevTier = this.currentTier;

    this.currentTier = tier;
    this.currentSlotSequence = newSeq;
    // Espelha no disco pro PRÓXIMO boot não precisar da rede pra saber o que tocar (medido: 1283ms
    // de tela apagada esperando o sync, a cada reciclagem). Só vale dentro da mesma janela horária —
    // a guarda está no `slotSequenceCache`, junto do porquê.
    persistSlotSequence(
      newSeq,
      tier === 2 || tier === 3 ? tier : null,
      this.canonicalEpochValue,
    );
    if (seqChanged) {
      const recentlyRestored =
        this.lastRestoreAt > 0 &&
        Date.now() - this.lastRestoreAt < PlaybackStore.POST_RESTORE_GUARD_MS;
      if (recentlyRestored) {
        console.log(
          '[PlaybackStore] setFromSync: preservando currentSlotIndex=' +
            this.currentSlotIndex +
            ' (restauração recente de trigger, guard de ' +
            PlaybackStore.POST_RESTORE_GUARD_MS +
            'ms ativo)',
        );
        // Apenas re-normaliza pra cobrir caso da sequência ter encolhido
        if (newSeq && newSeq.slots.length > 0) {
          const total = newSeq.slots.length;
          this.currentSlotIndex =
            ((this.currentSlotIndex % total) + total) % total;
        }
      } else {
        this.currentSlotIndex = 0;
      }
    }
    this.lastHydrationAt = new Date().toISOString();

    // CP10 — Track tier_switch quando o tier resolvido muda. Mantém o
    // analytics em dia pra dashboards de comportamento do player ("quando
    // esse player saiu do Tier 4 legado pra Tier 3?"). Fire-and-forget —
    // analytics nunca bloqueia hidratação do estado.
    if (tierChanged) {
      try {
        analyticsService.trackTierSwitch(prevTier, tier, 'sync_change');
      } catch {
        // Defensivo: analytics nunca pode quebrar o sync
      }
    }
  }

  /**
   * Avança pra próximo slot da sequência. Wrap-around (volta pro 0 ao
   * passar do último). Será chamado pelo loop de execução do CP9.
   *
   * No-op em Tier 1/4 (sem sequência) — não joga exception pra não
   * complicar callers que ainda não sabem qual tier o player está.
   */
  advanceSlot(): void {
    if (
      !this.currentSlotSequence ||
      this.currentSlotSequence.slots.length === 0
    ) {
      return;
    }
    const total = this.currentSlotSequence.slots.length;
    this.currentSlotIndex = (this.currentSlotIndex + 1) % total;
  }

  /**
   * Avança pro próximo slot imediatamente — atalho semântico pra ações
   * interativas (ex: botão SKIP_TO_NEXT). Delega pra {@link advanceSlot}.
   */
  skipCurrentSlot(): void {
    this.advanceSlot();
  }

  /**
   * Restaura o índice do slot pra um valor específico. Usado pelo
   * {@link triggerStore} pra preservar a posição na campanha quando
   * um trigger é disparado e depois desativado — sem isso, o próximo
   * sync após o trigger acabar re-aplica a playlist a partir de
   * {@link currentSlotIndex} stale (ficou no valor anterior ao
   * trigger), voltando o player pro slot 0 (Prada) em vez de retomar
   * no slot interrompido.
   *
   * Faz modulo defensivo se idx > sequência (sequência pode ter
   * encolhido entre o disparo e a retomada do trigger).
   *
   * No-op em Tier 1/4 (sem sequência).
   */
  restoreSlotIndex(idx: number): void {
    if (
      !this.currentSlotSequence ||
      this.currentSlotSequence.slots.length === 0
    ) {
      return;
    }
    const total = this.currentSlotSequence.slots.length;
    const normalized = ((idx % total) + total) % total; // safe modulo
    this.currentSlotIndex = normalized;
    // Ativa guard pra próximo setFromSync não resetar pra 0 (ver
    // setFromSync). Sem isso, sync subsequente ao trigger_deactivated
    // (que chega em ~1s) zera o slot que acabamos de restaurar.
    this.lastRestoreAt = Date.now();
  }

  /**
   * ÉPOCA DO SYNC CANÔNICO DA SEQUÊNCIA — o instante a que o slot 0 pertence.
   *
   * <p>O `scheduledOffsetS` de cada slot já é "segundos desde o `hourWindowStart`". Ou seja, a
   * sequência SEMPRE descreveu uma posição absoluta no relógio; o player é que nunca a lia —
   * entrava no slot 0 e caminhava, então dois players do mesmo grupo exclusivo acabavam em
   * pontos diferentes do loop só por terem bootado em momentos diferentes. Expor a época aqui
   * é o que liga a sequência à máquina de sync canônico que o Tier 4 já usa
   * ({@code resolveLoopTarget}): o `Stream` passa a pular pro slot certo E dar seek dentro do
   * vídeo, e a posição é reavaliada contra o relógio do servidor — então fechar o app, recarregar
   * ou reiniciar reencontra o grupo em vez de recomeçar.</p>
   *
   * <p>`null` quando o server NÃO ligou o sync canônico pra este player: aí a frota Tier 3
   * normal segue exatamente como antes (começa no slot 0). O portão é do server, não nosso —
   * é ele que sabe se o grupo inteiro recebe a mesma composição.</p>
   *
   * <p>🔴 <b>É o `playback_started_at`, NÃO o `hourWindowStart`.</b> A primeira versão usava a
   * janela horária, porque é dela que o `scheduledOffsetS` é medido. Parecia mais correto e
   * estava errado, por dois motivos medidos em produção (grupo 15, 2026-10-07):</p>
   *
   * <ol>
   *   <li><b>Duas épocas na mesma frota.</b> O caminho legado (Tier 4) ancora no
   *       `playback_started_at` que vem em `files[]`. Com a janela aqui, os dois caminhos
   *       ficavam defasados — 476s no dia da medição — e um peer em cada caminho NUNCA se
   *       encontrava.</li>
   *   <li><b>A janela se move; o loop não.</b> A janela anda 3600s por hora e o loop tinha
   *       3514s: `3600 mod 3514 = 86s` de pulo de fase a cada virada. Peers que renovam a
   *       sequência em lados opostos da virada ficam 86s distantes <i>permanentemente</i>,
   *       até os dois renovarem — que é como um player "volta e não reencontra o grupo".</li>
   * </ol>
   *
   * <p>O que se perde: o slot deixa de cair exatamente no `scheduledOffsetS` do relógio de
   * parede, então o "▶ AGORA" do admin pode discordar do que o player toca. É cosmético perto
   * de videowall fora de fase. O que importa pro grupo é que TODOS concordem, e só há uma
   * época que todos já compartilham.</p>
   */
  get canonicalEpoch(): string | null {
    return this.canonicalEpochValue;
  }

  /**
   * Slot ativo agora (computed). Null em Tier 1/4 ou quando a sequência
   * está vazia (defensivo — backend não deveria mandar sequência vazia).
   */
  get currentSlot() {
    const seq = this.currentSlotSequence;
    if (!seq || seq.slots.length === 0) return null;
    // Defensivo: slotIndex pode ter ficado out-of-bounds se a sequência
    // encolheu entre 2 syncs. Faz modulo pra ficar safe.
    const idx = this.currentSlotIndex % seq.slots.length;
    return seq.slots[idx];
  }

  /**
   * Indica se o player está em modo "slot_sequence" (Tier 2 ou 3).
   * Helper pra outros stores/services decidirem se devem ler de
   * {@link StreamStore} (legado) ou desta store (CP9+).
   */
  get isPlaylistMode(): boolean {
    return this.currentTier === 2 || this.currentTier === 3;
  }

  /**
   * Sprint 2 CP9 — converte os slots da sequência ativa em
   * {@link PlaylistItem}[] consumível pelo {@link StreamStore}. Permite
   * que o loop legado do streamStore (playNext, playlistVersion,
   * idempotência) tome conta sem refactor.
   *
   * <p>Mapeamento snake↔camel resumido:
   * <ul>
   *   <li>{@code Slot.contentId} → {@code PlaylistItem.id}</li>
   *   <li>{@code Slot.contentName} → {@code PlaylistItem.name}</li>
   *   <li>{@code Slot.contentType} → {@code PlaylistItem.type}</li>
   *   <li>{@code Slot.contentUrl} → {@code PlaylistItem.url}</li>
   *   <li>{@code Slot.durationS} → {@code PlaylistItem.duration} +
   *       {@code PlaylistItem.hasDurationOverride} (só true quando server
   *       envia {@code has_duration_override} — override explícito do operador)</li>
   *   <li>{@code Slot.programacaoId} → {@code PlaylistItem.campanhaId}</li>
   * </ul>
   *
   * <p>Campos do PlaylistItem que slot_sequence NÃO traz:
   * fileSize/checksum/mimeType/startAt/endAt/daysOfWeek.
   * Backend já filtrou validade horária/dias antes de gerar o slot, e
   * o stream consumer não precisa dos demais nessa fase. Valores
   * default conservadores: fileSize=0, checksum='' (cache valida só
   * quando entrega via files[] legado).</p>
   *
   * <p>Array vazio quando não há sequência ativa.</p>
   */
  /**
   * Quando o server devolve tier=2/3 com {@code slots=[]} (filtro de
   * orientação no SlotSequenceService) mas o scheduler ainda tem itens
   * tocáveis, sintetiza slots a partir da playlist ativa do scheduler.
   * Evita cair em {@code scheduler_backup} — mesma fonte, índices estáveis.
   */
  hydrateSlotsFromPlaylistItems(items: PlaylistItem[]): boolean {
    const seq = this.currentSlotSequence;
    if (!seq || seq.slots.length > 0 || items.length === 0) {
      return false;
    }

    let offsetS = 0;
    const slots: Slot[] = items.map((item, sequence) => {
      const durationS = item.duration ?? 0;
      const slot: Slot = {
        sequence,
        scheduledOffsetS: offsetS,
        durationS,
        hasDurationOverride: item.hasDurationOverride === true,
        contentId: item.id,
        contentName: item.name,
        contentType:
          item.type === 'image'
            ? 'image'
            : item.type === 'stream'
              ? 'stream'
              : 'video',
        contentUrl: item.url,
        streamUrl: item.streamUrl,
        programacaoId: Number(item.campanhaId) || 0,
        zonaId: item.zonaId,
        // V088 — simétrico com toPlaylistItems: preserva clima+validade+dia/hora
        // ao hidratar slots do scheduler (fallback quando server manda slots=[]).
        weatherConditionContent: item.weatherConditionContent ?? null,
        validFrom: item.validFrom,
        validUntil: item.validUntil,
        startAt: item.startAt,
        endAt: item.endAt,
        daysOfWeek: item.daysOfWeek,
        scheduleMode: item.scheduleMode,
        weeklySchedule: item.weeklySchedule,
      };
      offsetS += durationS;
      return slot;
    });

    this.currentSlotSequence = {
      ...seq,
      slots,
      totalDurationS: offsetS,
    };
    otaLog.warn('PlaybackStore: slots hidratados do scheduler', {
      playlistId: seq.playlistId,
      count: slots.length,
    });
    return true;
  }

  toPlaylistItems(): PlaylistItem[] {
    if (!this.currentSlotSequence) return [];
    // Época do loop (ou undefined). Resolvida UMA vez: é a mesma pra todos os slots —
    // é o loop inteiro que está ancorado, não cada item.
    const playbackStartedAt = this.canonicalEpoch ?? undefined;
    return this.currentSlotSequence.slots.map<PlaylistItem>((slot) => ({
      id: slot.contentId,
      type: slot.contentType,
      name: slot.contentName,
      url: slot.contentUrl,
      // Stream: URL nativa (m3u8/mpd/rtsp/YouTube). Sem propagar aqui, o Tier 2/3
      // perdia o streamUrl → C1 do Stream cai no else → AvplayVideo(url=null) → freeze na TV.
      streamUrl: slot.streamUrl,
      duration: slot.durationS,
      // Propaga flag explícita do server — false/ausente = duração natural (onEnded).
      // Players antigos ignoram has_duration_override no JSON e hardcodavam true (bug).
      hasDurationOverride: slot.hasDurationOverride === true,
      fileSize: 0,
      checksum: '',
      mimeType: slot.contentType === 'image' ? 'image/jpeg' : 'video/mp4',
      campanhaId: slot.programacaoId,
      zonaId: slot.zonaId ?? undefined,
      // Carimbo que liga o Tier 2/3 ao sync canônico (vide `canonicalEpoch`). Sem ele o
      // StreamStore/Stream não tinham âncora nenhuma nesta via e todo o realinhamento
      // (índice inicial, seek no canplay, encurtar imagem, pulso de deriva) ficava inerte.
      playbackStartedAt,
      // V088 (2026-08-08) — clima + validade + dia/hora do CONTEÚDO (paridade Tier 4).
      // Sem propagar aqui, o filtro `filterPlaylistBySchedule` e o AND weather
      // ficavam com todos os campos undefined → passavam tudo.
      // Regras-temporais-player.md R0/R2/R4.
      weatherConditionContent: slot.weatherConditionContent ?? null,
      validFrom: slot.validFrom ?? undefined,
      validUntil: slot.validUntil ?? undefined,
      startAt: slot.startAt ?? undefined,
      endAt: slot.endAt ?? undefined,
      daysOfWeek: slot.daysOfWeek ?? undefined,
      scheduleMode: slot.scheduleMode ?? undefined,
      weeklySchedule: slot.weeklySchedule ?? undefined,
    }));
  }

  /**
   * Hidrata a sequência a partir do CACHE EM DISCO, no boot, antes de qualquer sync.
   *
   * <p>Deliberadamente NÃO reusa `setFromSync`: aquele caminho compara com a sequência anterior,
   * decide reset de índice, emite analytics de troca de tier e reage a slots vazios — tudo
   * pressupondo "chegou coisa nova do server". Aqui não chegou nada: estamos apenas devolvendo o que
   * esta mesma TV já estava tocando segundos antes. Reaproveitar aquela lógica geraria eventos de
   * mudança falsos.
   *
   * <p>`currentSlotIndex` fica em 0 de propósito: o índice de onde parar/retomar é do `streamStore`
   * (e o sync que vem logo atrás reposiciona pelo caminho normal). Não inventamos posição.
   *
   * <p>Não grava de volta no cache — o dado veio de lá.
   */
  restoreFromCache(
    seq: SlotSequence,
    tier: 2 | 3,
    epoch: string | null = null,
  ): void {
    this.currentTier = tier;
    this.currentSlotSequence = seq;
    this.currentSlotIndex = 0;
    // Veio gravado junto com a sequência. É o que permite o boot offline de um player de
    // videowall já entrar em fase, em vez de tocar o slot 0 até o primeiro sync responder.
    this.canonicalEpochValue = epoch;
    this.lastHydrationAt = new Date().toISOString();
  }

  /**
   * Reseta a store pro estado inicial. Útil em logout / re-pairing.
   */
  reset(): void {
    this.currentTier = 4;
    this.currentSlotSequence = null;
    this.currentSlotIndex = 0;
    this.canonicalEpochValue = null;
    this.lastHydrationAt = null;
    // Programação em disco pertence ao pareamento atual — em logout/re-pairing ela não pode
    // sobreviver, senão o próximo boot toca a playlist do tenant anterior antes do 1º sync.
    clearSlotSequenceCache();
  }

  /**
   * True quando a sequência mudou de playlist. Null transitions também contam
   * (Tier mudou e a sequência sumiu/apareceu).
   *
   * <p>`hourWindowStart` fica de fora de propósito: em grupo exclusivo o servidor
   * gera a MESMA sequência hora após hora (só esse carimbo muda), e a posição vem
   * da época — não há nada a resetar na virada da hora.</p>
   */
  private didSequenceChange(
    prev: SlotSequence | null,
    next: SlotSequence | null,
  ): boolean {
    if (prev === null && next === null) return false;
    if (prev === null || next === null) return true;
    return prev.playlistId !== next.playlistId;
  }

  /**
   * True quando os slots mudaram: outro conteúdo (ex.: troca de orientação
   * regenera a sequência) OU outra duração (muda o tamanho do loop e, com ele,
   * o lugar de cada slot no relógio). playlistId igual não basta nesse caso.
   */
  private didSequenceContentChange(
    prev: SlotSequence | null,
    next: SlotSequence | null,
  ): boolean {
    if (prev === null && next === null) return false;
    if (prev === null || next === null) return true;
    if (prev.slots.length !== next.slots.length) return true;
    return prev.slots.some((slot, index) => {
      const other = next.slots[index];
      return (
        slot.contentId !== other?.contentId ||
        slot.durationS !== other?.durationS
      );
    });
  }
}

/**
 * Singleton exportado direto do arquivo pra outros stores importarem sem
 * passar por {@code stores/index.ts} (evita ciclo de import — mesmo padrão
 * do {@code kiosk.store} e {@code trigger.store}).
 */
export const playbackStore = new PlaybackStore();

export default PlaybackStore;
